import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaSave } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import StaffStatus from './StaffStatus';

// Global Admin → GoHighLevel. Platform-level GHL configuration: the platform's OWN GHL
// account, used to track every tenant and their app tier (Community vs Pro) so the marketing
// team can work them. This is NOT a tenant's own GHL/lead-magnet integration.
function GhlSettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [ghl, setGhl] = useState({
    enabled: false,
    has_api_token: false,
    masked_api_token: '',
    location_id: '',
    api_version: '2021-07-28',
    community_tag: 'pipsqueak community',
    pro_tag: 'pipsqueak pro user'
  });
  const [apiToken, setApiToken] = useState('');

  const adminToken = localStorage.getItem('Admintoken');

  const loadGhl = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/ghl-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load GoHighLevel settings');
      if (data.settings) setGhl(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGhl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveGhl = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/ghl-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          enabled: ghl.enabled,
          locationId: ghl.location_id || '',
          apiVersion: ghl.api_version || '2021-07-28',
          communityTag: ghl.community_tag || 'pipsqueak community',
          proTag: ghl.pro_tag || 'pipsqueak pro user',
          // Only send the token when the admin typed a new value (blank = keep existing).
          apiToken: apiToken.trim() || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save GoHighLevel settings');
      setGhl(data.settings);
      setApiToken('');
      setMessage('GoHighLevel settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const isConfigured = ghl.enabled && ghl.has_api_token && ghl.location_id;

  return (
    <div className={isDarkMode ? 'text-light' : ''}>
      <StaffStatus on={isConfigured}>
        {isConfigured ? 'Enabled' : 'Disabled'}
      </StaffStatus>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Card className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Card.Body>
          <Row className="g-4">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Private Integration Token</Form.Label>
                <Form.Control
                  type="password"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder={ghl.has_api_token ? `Saved (${ghl.masked_api_token || '••••'}). Enter a new token to replace it.` : 'pit-…'}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  From your GHL → Settings → Private Integrations. Needs Contacts (write) &amp; tags scopes. Stored encrypted server-side; never returned to the browser.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Location ID</Form.Label>
                <Form.Control
                  value={ghl.location_id || ''}
                  onChange={(e) => setGhl(prev => ({ ...prev, location_id: e.target.value }))}
                  placeholder="e.g. abcdEFGH1234"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  The GHL sub-account (location) that holds your tenant contacts.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>API Version</Form.Label>
                <Form.Control
                  value={ghl.api_version || ''}
                  onChange={(e) => setGhl(prev => ({ ...prev, api_version: e.target.value }))}
                  placeholder="2021-07-28"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Community Tag</Form.Label>
                <Form.Control
                  value={ghl.community_tag || ''}
                  onChange={(e) => setGhl(prev => ({ ...prev, community_tag: e.target.value }))}
                  placeholder="pipsqueak community"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Pro Tag</Form.Label>
                <Form.Control
                  value={ghl.pro_tag || ''}
                  onChange={(e) => setGhl(prev => ({ ...prev, pro_tag: e.target.value }))}
                  placeholder="pipsqueak pro user"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="ghl-sync-enabled"
                label="Enable syncing tenants to GoHighLevel"
                checked={!!ghl.enabled}
                onChange={(e) => setGhl(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button onClick={saveGhl} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              Save GoHighLevel Settings
            </Button>
          </div>

          <p className={`mt-3 mb-0 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
            To sync all existing tenants at once, run the backfill script:
            <code className="ms-1">node scripts/backfill_ghl_tenants.js</code>.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

export default GhlSettings;
